import { useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const MessageSearch = ({ messages, onSelectMessage }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    
    // Rechercher dans les messages
    const results = messages.filter(message =>
      message.content.toLowerCase().includes(query.toLowerCase())
    );
    
    setSearchResults(results);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  };

  const formatMessagePreview = (content, query) => {
    const index = content.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return content;

    const start = Math.max(0, index - 30);
    const end = Math.min(content.length, index + query.length + 30);
    const preview = content.substring(start, end);
    
    return {
      before: preview.substring(0, index - start),
      match: preview.substring(index - start, index - start + query.length),
      after: preview.substring(index - start + query.length)
    };
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="border-b bg-white">
      <div className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Rechercher dans les messages..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Résultats de recherche */}
      {isSearching && (
        <div className="border-t max-h-96 overflow-y-auto">
          {searchResults.length > 0 ? (
            <div className="p-2">
              <div className="text-sm text-gray-600 px-2 py-1 mb-2">
                {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
              </div>
              {searchResults.map((message) => {
                const preview = formatMessagePreview(message.content, searchQuery);
                return (
                  <button
                    key={message.id}
                    onClick={() => {
                      onSelectMessage(message.id);
                      handleClearSearch();
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">
                        {message.sender.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {formatTime(message.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {preview.before && <span>...{preview.before}</span>}
                      <span className="bg-yellow-200 font-medium">{preview.match}</span>
                      {preview.after && <span>{preview.after}...</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>Aucun message trouvé</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageSearch;
